import {
  CheckBox,
  DatePicker,
  DropDownMenu,
  RadioButton,
} from "@etframework/ui.components";
import {
  forwardRef,
  Fragment,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";

const actions = {
  SpecificDate: {
    name: "SpecificDate",
    field: "TaskTaxReturnInfo.TaxDeadline",
    taskField: "DueDate",
  },
  ApplyExtension: {
    name: "ApplyExtension",
    field: "TaskTaxReturnInfo.ExtendedTaxDeadline_ApplyExtension",
    taskField: "DueDate_ApplyExtension",
    constValue: null,
  },
  ApplySpecificExtension: {
    name: "ApplySpecificExtension",
    field: "TaskTaxReturnInfo.ExtendedTaxDeadline",
    taskField: "DueDate",
  },
  RemoveExtension: {
    name: "RemoveExtension",
    field: "TaskTaxReturnInfo.ExtendedTaxDeadline",
    taskField: "DueDate_RemoveExtension",
    constValue: null,
  },
};

const TaxDeadlineTabInput = forwardRef(
  (
    {
      id,
      value,
      field,
      change,
      changeAction,
      settings,
      action: _action = actions.SpecificDate.name,
      className,
      updateDueDateDisabled,
    },
    ref
  ) => {
    const { t, i18n } = useTranslation(["BulkUpdateTasksModal", "Shared"]);
    const [action, setAction] = useState(_action);
    const [subAction, setSubAction] = useState(
      _action === actions.ApplySpecificExtension.name ? "Date" : "Rule"
    );

    const [updateDueDate, setUpdateDueDate] = useState(
      value?.updateDueDate ?? false
    );

    const dateRef = useRef(null);

    const updateDueDateCheckbox = (
      <div className="pt-3">
        <CheckBox
          checked={updateDueDate}
          change={(e) => setUpdateDueDate(e.checked)}
          value={"updateDueDate"}
          label={t("TaxDeadline.UpdateDueDate")}
          disabled={updateDueDateDisabled}
        />
      </div>
    );

    const tab1 = (
      <Fragment>
        <DatePicker
          ref={dateRef}
          labelText={t("TaxDeadline.Date")}
          className={"tax-date-picker"}
          id={id}
          value={value?.dateValue ?? null}
          change={change}
          language={i18n.language}
        />
        {updateDueDateCheckbox}
      </Fragment>
    );

    const tab2Rule = (
      <Fragment>
        <div className="p-3 mt-4 rounded bg-et-lightest-gray">
          {t("TaxDeadline.ApplyExtensionDescription")}
        </div>
        <div className="fs-12 mt-4">
          {t("TaxDeadline.ApplyExtensionWarning")}
        </div>
      </Fragment>
    );

    const tab2Date = (
      <Fragment>
        <DatePicker
          ref={dateRef}
          className={"tax-date-picker mt-2"}
          id={id}
          value={value?.dateValue ?? null}
          change={change}
          language={i18n.language}
        />
      </Fragment>
    );

    const tab2DDItems = {
      Rule: {
        id: "Rule",
        action: "ApplyExtension",
        component: tab2Rule,
        text: t("TaxDeadline.Rule"),
        iconCss: subAction === "Rule" ? "fa-regular fa-check" : "",
      },
      Date: {
        id: "Date",
        component: tab2Date,
        action: "ApplySpecificExtension",
        text: t("TaxDeadline.Date"),
        iconCss: subAction === "Date" ? "fa-regular fa-check" : "",
      },
    };

    const onSelectRuleOrDate = (e) => {
      const item = tab2DDItems[e.properties.id];
      setSubAction(item.id);
      setAction(item.action);
    };

    const tab2 = (
      <Fragment>
        <div className="d-flex align-items-center">
          <span>{t("TaxDeadline.ApplyExtension")}</span>
          <DropDownMenu
            items={Object.values(tab2DDItems)}
            select={onSelectRuleOrDate}
          >
            {tab2DDItems[subAction].text}
          </DropDownMenu>
        </div>
        {tab2DDItems[subAction].component}
        <div className="mt-2">{updateDueDateCheckbox}</div>
      </Fragment>
    );

    const tab3 = (
      <Fragment>
        <span className="fw-600">{t("TaxDeadline.RemoveExtension")}</span>
        <div className="p-3 rounded bg-et-lightest-gray mt-1">
          {t("TaxDeadline.RemoveExtensionDescription")}
        </div>
        <div className="mt-2">{updateDueDateCheckbox}</div>
      </Fragment>
    );

    const onChangeAction = (e) => {
      if (e.value) {
        setAction(e.value);
        changeAction?.(e.value);
      }
    };

    const applyValue = useCallback(
      (properties) => {
        const currentAction = actions[action];
        const value = dateRef.current?.getValue();

        switch (currentAction) {
          case actions.ApplyExtension:
          case actions.RemoveExtension:
            properties[currentAction.field] = currentAction.constValue;
            if (updateDueDate)
              properties[currentAction.taskField] = currentAction.constValue;
            break;

          case actions.SpecificDate:
          case actions.ApplySpecificExtension:
            if (value) {
              properties[currentAction.field] = dateRef.current?.getValue();
              if (updateDueDate)
                properties[currentAction.taskField] =
                  dateRef.current?.getValue();
            }
            break;
        }
      },
      [action, updateDueDate, dateRef]
    );

    const getValue = useCallback(() => {
      const value = {
        dateValue: dateRef.current?.getValue() ?? null,
        updateDueDate: updateDueDate,
      };
      const hasValue =
        action === actions.ApplyExtension.name ||
        action === actions.RemoveExtension.name
          ? value.dateValue === null
          : !!value.dateValue;

      return {
        field: field,
        action: action,
        value: value,
        hasValue: hasValue,
        settings: settings,
      };
    }, [updateDueDate, field, action, settings]);

    useImperativeHandle(
      ref,
      () => ({
        applyValue,
        getValue,
        focus: dateRef.current?.getComponentRef().focusIn,
      }),
      [applyValue, getValue, dateRef]
    );

    useEffect(() => {
      updateDueDateDisabled && setUpdateDueDate(false);
    }, [updateDueDateDisabled]);

    const tabs = {
      [actions.SpecificDate.name]: tab1,
      [actions.ApplyExtension.name]: tab2,
      [actions.ApplySpecificExtension.name]: tab2,
      [actions.RemoveExtension.name]: tab3,
    };

    return (
      <div
        id={id}
        className={"deadline-tab-input rounded border border-et-light-gray d-flex w-100".concat(
          " ",
          className
        )}
      >
        <div className="bg-et-lightest-gray p-2 d-flex flex-column gap-2 mw-fit">
          <RadioButton
            id={id + "_date-radio-button"}
            containerClass={"p-2"}
            name={id}
            label={t("TaxDeadline.SpecificDate")}
            value={"SpecificDate"}
            checked={action === "SpecificDate"}
            onChange={onChangeAction}
          />
          <RadioButton
            id={id + "_apply-extension-radio-button"}
            name={id}
            containerClass={"p-2"}
            label={t("TaxDeadline.ApplyExtension")}
            value={"ApplyExtension"}
            checked={
              action === "ApplyExtension" || action === "ApplySpecificExtension"
            }
            onChange={onChangeAction}
          />
          <RadioButton
            id={id + "_remove-extension-radio-button"}
            name={id}
            containerClass={"p-2"}
            label={t("TaxDeadline.RemoveExtension")}
            value={"RemoveExtension"}
            checked={action === "RemoveExtension"}
            onChange={onChangeAction}
          />
        </div>
        <div className="bg-et-white flex-grow-1 p-3">{tabs[action]}</div>
      </div>
    );
  }
);

TaxDeadlineTabInput.actions = actions;

export { TaxDeadlineTabInput };
